/**
 * Button — DS-core rebuild of the Framer buttons (named tokens, no raw hex).
 *   solid (Button 06)  — accent pill, padding 8/8/8/24, gap 24, white arrow circle.
 *   ghost (White Border Button) — translucent white + blur, padding 8/24, no arrow.
 * Renders an internal Link (relative `to`) or an `<a>` (external/anchor href).
 */
import { m } from "motion/react";
import { LocaleLink } from "../../i18n/LocaleLink";
import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";

type Variant = "solid" | "solid-light" | "ghost" | "outline";

const BASE =
  "group inline-flex h-11 items-center rounded-full font-ds text-ds-14 font-semibold uppercase leading-none tracking-[0.02em] transition-colors duration-200 cursor-pointer select-none";

const VARIANTS: Record<Variant, string> = {
  solid: "bg-ds-accent text-white hover:bg-ds-accent-hover",
  // Reverse of solid for dark/photo backgrounds — white pill, accent text + accent arrow circle.
  "solid-light": "bg-white text-ds-accent hover:bg-white/90",
  ghost: "px-6 bg-white/30 text-ds-on-dark backdrop-blur-[10px] hover:bg-white/40",
  // Light secondary — same branded pill, bordered (works on light surfaces).
  outline: "justify-center px-6 bg-white text-ink border border-line-strong hover:border-ink-soft hover:bg-bg-subtle",
};

const ARROWED = new Set<Variant>(["solid", "solid-light"]);

const isExternal = (href: string) => /^(https?:|mailto:|tel:)/.test(href);

export function Button({
  children,
  variant = "solid",
  to = "#",
  className = "",
  arrow = true,
}: {
  children: ReactNode;
  variant?: Variant;
  /** Relative route ("/features") → Link; external/anchor → <a>. */
  to?: string;
  /** Extra classes (e.g. `w-full justify-between` for a full-width CTA). */
  className?: string;
  /** solid only: show the trailing arrow circle (default true). */
  arrow?: boolean;
}) {
  const showArrow = ARROWED.has(variant) && arrow;
  // solid variants carry the arrow → asymmetric padding; without it, symmetric + centred.
  const solidPad = ARROWED.has(variant) ? (showArrow ? "gap-6 pl-6 pr-2" : "justify-center px-6") : "";
  const cls = `${BASE} ${VARIANTS[variant]} ${solidPad} ${className}`;
  // Reverse the arrow circle for the light variant (accent circle, white glyph).
  const circleCls = variant === "solid-light" ? "bg-ds-accent text-white" : "bg-white text-ds-accent";
  const arrowEl = showArrow && (
    <span className={`grid h-7 w-7 place-items-center rounded-full ${circleCls}`}>
      <ArrowUpRight
        size={15}
        strokeWidth={2.5}
        className="transition-transform duration-200 ease-out group-hover:translate-x-[1px] group-hover:-translate-y-[1px]"
      />
    </span>
  );

  if (isExternal(to) || to.startsWith("#")) {
    return (
      <m.a
        href={to}
        className={cls}
        whileTap={{ scale: 0.97 }}
        target={isExternal(to) ? "_blank" : undefined}
        rel={isExternal(to) ? "noopener noreferrer" : undefined}
      >
        {children}
        {arrowEl}
      </m.a>
    );
  }
  return (
    <LocaleLink to={to} className={cls}>
      {children}
      {arrowEl}
    </LocaleLink>
  );
}
